import { SendOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Input, Popover, Row, Upload, UploadFile, UploadProps } from 'antd';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import grispiApi from '../../../apis/grispiApi';
import { sendMessage } from '../../../clients/chat/connection';
import { WsMessage } from '../../../clients/chat/types';
import useFileSizeCompare from '../../../hooks/useFileSizeCompare';
import { UploadFilesResponse } from '../../../services/upload/uploadFiles';
import { generateTicketFormNameForStorage } from '../../../store/childStore/tabStore';
import { useCurrentUserStore } from '../../../store/RootStoreContext';
import { grispiNotification } from '../../../store/useNotificationStore';
import { getTenantId } from '../../../utils/accountHelper';
import { getSelectedTabKeyFromUrl } from '../../../utils/routeHelper';
import { fireResetRichTextEditorStateEvent } from '../../GrispiRichTextEditor/plugins/ResetEditorStatePlugin';
import QuillGrispiRichTextEditor from '../../QuillGrispiRichTextEditor';
import { GrispiFile, UploadProgressEvent } from '../../UploadFilesList';
import { ACCEPTED_ALL_TYPE_LIST, CURRENT_USER_TEMP_MESSAGE_ID } from './AgentChatComponent';
import { ContentTypeMap } from './types';

const styles = {
    input: { paddingTop: '0.35rem', paddingBottom: '0.35rem', backgroundColor: 'white' }
};

interface Props {
    chatSessionId: string;
    isDisabled: boolean;
}
// style={{ marginRight: 8 }} />
function UploadButton({
    disabled = false,
    onUploadFile,
    uploadUrl
}: {
    disabled?: boolean;
    onUploadFile: (file: UploadFile) => void;
    uploadUrl: string;
}) {
    const { t } = useTranslation('forms');

    // TODO: cagri, chat-upload => should check fileSizeLimit from company-settings or somehere else
    const { role } = useCurrentUserStore();
    const { compareFn, fileSizeLimit } = useFileSizeCompare(role);
    const abortControllerRef = useRef<AbortController>();

    const [isUploading, setIsUploading] = useState(false);

    const uploadImage: UploadProps<GrispiFile>['customRequest'] = async (options) => {
        const { onSuccess, onError, file, onProgress } = options;
        abortControllerRef.current = new AbortController();

        const fmData = new FormData();
        const config: AxiosRequestConfig = {
            headers: { 'content-type': 'multipart/form-data' },
            onUploadProgress: (event) => {
                onProgress({ percent: (event.loaded / event.total) * 100 } as UploadProgressEvent);
            },
            signal: abortControllerRef.current.signal
            // cancelToken
        };
        fmData.append('file', file);

        try {
            const res: AxiosResponse<UploadFilesResponse[]> = await grispiApi.post(uploadUrl, fmData, config);

            onSuccess(res.data, res.request);
        } catch (err: any) {
            onError(err, err.response?.data?.message);
        }
    };

    const handleBeforeUpload: UploadProps['beforeUpload'] = (_file) => {
        const fileSize = _file.size / 1024 / 1024;

        if (compareFn(fileSize)) {
            grispiNotification('error', t('attachment_big', { maxSize: fileSizeLimit }), 'error');
            return false || Upload.LIST_IGNORE;
        }
    };

    const handleOnChange: UploadProps['onChange'] = ({ file }) => {
        file.status === 'uploading' && setIsUploading(true);
        if (file.status === 'error') {
            const isCancelled = file.error?.code === 'ERR_CANCELED';
            const errorMessage = isCancelled ? t('attachment_canceled', { fileName: file.name }) : file.response;
            grispiNotification('error', errorMessage, 'error');
            setIsUploading(false);
        }
        if (file.status === 'done') {
            setIsUploading(false);
            onUploadFile(file);
        }
    };
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    return (
        <>
            <div id="chat-upload-button">
                <Upload
                    accept={ACCEPTED_ALL_TYPE_LIST.toString()}
                    beforeUpload={handleBeforeUpload}
                    customRequest={uploadImage}
                    name="file"
                    onChange={handleOnChange}
                    // {...antdProps}
                >
                    <Button
                        disabled={disabled}
                        icon={<UploadOutlined />}
                        loading={isUploading}
                        shape="circle"
                        onClick={(e) => {
                            e.preventDefault();
                        }}
                        //size="small"
                        //type ="default"
                    />
                </Upload>
            </div>
        </>
    );
}

export interface MediaFileMeta {
    fileName: string;
    mimeType: string;
    url: string;
    size: number; // in bytes
}
function getMediaFileMetaData(file: Pick<UploadFilesResponse, 'fileName' | 'mimeType' | 'publicUrl' | 'size'>) {
    const { fileName, mimeType, publicUrl, size } = file;
    const metaData: MediaFileMeta = { fileName, mimeType, size, url: publicUrl };
    return JSON.stringify(metaData);
}

export default function ChatInput({ chatSessionId, isDisabled }: Props) {
    const { t } = useTranslation('tickets');
    const { asPath } = useRouter();
    const { chatUsername } = useCurrentUserStore();
    const destination = `/app/chat/${getTenantId()}/${chatSessionId}`;

    const canSendMessage = !isDisabled;
    const [value, setValue] = useState('');
    const [status, setStatus] = useState(false);

    const sendChatMessage = useCallback(
        (value: string | Pick<UploadFilesResponse, 'fileName' | 'mimeType' | 'objectThumbUrl' | 'size'>) => {
            if (canSendMessage && value) {
                const contentType = typeof value === 'string' ? ContentTypeMap['text/html'] : value.mimeType;
                const message = typeof value === 'string' ? value?.trim() : getMediaFileMetaData(value);

                const messageBody = {
                    contentType,
                    sender: chatUsername(),
                    sentAt: Date.now(),
                    text: message,
                    id: CURRENT_USER_TEMP_MESSAGE_ID
                } as WsMessage;

                sendMessage(messageBody, destination, chatSessionId);
                if (contentType === 'text/html') {
                    setValue('');
                    fireResetRichTextEditorStateEvent({
                        eventListenerKey: `chat-input-${generateTicketFormNameForStorage(getSelectedTabKeyFromUrl(asPath))}`
                    });
                }
                setStatus(false);
            } else {
                setStatus(true);
            }
        },
        [canSendMessage, destination, chatSessionId, chatUsername, asPath]
    );

    return (
        <Row style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
            {isDisabled ? (
                <Popover placement="top" content={t('chatEndedPopover')} trigger={['hover']}>
                    <Input
                        id="agent-chat-input-disabled" // to fix border-color css issue on hover/focus etc.
                        className="agent-chat-input-disabled"
                        disabled={isDisabled}
                        placeholder={t('placeholders.chatInput')}
                        suffix={
                            <Button
                                disabled
                                icon={<SendOutlined />}
                                shape="circle"
                                style={{ border: 'none', backgroundColor: 'none' }}
                                type="text"
                            />
                        }
                        style={styles.input}
                        // value={value}
                    />
                </Popover>
            ) : (
                <QuillGrispiRichTextEditor
                    value={value}
                    onChange={setValue}
                    chatInput
                    chatInputSendButton={
                        <Button
                            icon={<SendOutlined />}
                            onClick={() => sendChatMessage(value)}
                            shape="circle"
                            style={{
                                border: 'none',
                                backgroundColor: 'none',
                                position: 'absolute',
                                right: 15,
                                top: `calc(50% ${isDisabled ? '- 15' : '+ 6'}px)`,
                                cursor: 'grab'
                            }}
                            type="text"
                        />
                    }
                    chatInputUploadButton={
                        <UploadButton
                            onUploadFile={(file: UploadFile) => {
                                sendChatMessage(file.response);
                            }}
                            uploadUrl={`/chats/${chatSessionId}/attachment`}
                        />
                    }
                    error={status}
                    eventListenerKey={`chat-input-${generateTicketFormNameForStorage(
                        getSelectedTabKeyFromUrl(asPath)
                    )}`}
                />
            )}
        </Row>
    );
}
